import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageToggle } from './LanguageToggle';
interface NavigationProps {
  onScrollToSection: (sectionId: string) => void;
}
export function Navigation({
  onScrollToSection
}: NavigationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    t
  } = useLanguage();
  const navItems = [{
    label: t('nav.home'),
    id: 'hero'
  }, {
    label: t('nav.spices'),
    id: 'spices'
  }, {
    label: t('nav.howItWorks'),
    id: 'how-it-works'
  }, {
    label: t('nav.whyMushrooms'),
    id: 'problem-proof'
  }];
  const socialLinks = [{
    name: 'Instagram',
    url: 'https://instagram.com/thisisneurospice',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-279">
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" data-id="element-280" />
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" data-id="element-281" />
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" data-id="element-282" />
        </svg>
  }, {
    name: 'TikTok',
    url: 'https://tiktok.com/@thisisneurospice',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" data-id="element-283">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" data-id="element-284" />
        </svg>
  }, {
    name: 'LinkedIn',
    url: 'https://linkedin.com/company/thisisneurospice',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-285">
          <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" data-id="element-286" />
          <rect x="2" y="9" width="4" height="12" data-id="element-287" />
          <circle cx="4" cy="4" r="2" data-id="element-288" />
        </svg>
  }];
  const handleNavClick = (id: string) => {
    if (id === 'hero') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      onScrollToSection(id);
    }
    setIsOpen(false);
  };
  return <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-2 border-black shadow-md" data-id="element-289">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-290">
        <button onClick={() => handleNavClick('hero')} className="text-2xl font-black uppercase tracking-tighter hover:text-gray-600 transition-colors" data-id="element-291">
          NeuroSpice
        </button>
        {/* Desktop Navigation with CTAs */}
        <div className="hidden lg:flex items-center gap-6" data-id="element-292">
          <div className="flex items-center gap-1" data-id="element-293">
            {navItems.map(item => <button key={item.id} onClick={() => handleNavClick(item.id)} className="px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-100 transition-all duration-200" data-id="element-294">
                {item.label}
              </button>)}
          </div>
          {/* Primary CTA in header */}
          <button onClick={() => handleNavClick('waitlist')} className="px-6 py-2 bg-black text-white font-black text-xs uppercase tracking-wider hover:bg-gray-900 transition-all duration-200 rounded-full" data-id="element-295">
            {t('nav.joinWaitlist')}
          </button>
          {/* Language Toggle */}
          <LanguageToggle data-id="element-296" />
          {/* Social Icons */}
          <div className="flex items-center gap-3 ml-2 pl-4 border-l border-gray-300" data-id="element-297">
            {socialLinks.map(social => <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" className="text-black hover:text-gray-600 transition-all duration-200 hover:scale-110" aria-label={social.name} data-id="element-298">
                {social.icon}
              </a>)}
          </div>
        </div>
        {/* Mobile Menu Button */}
        <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden p-2 border-2 border-black hover:bg-black hover:text-white transition-all duration-200" aria-label="Toggle menu" data-id="element-299">
          {isOpen ? <XIcon className="w-6 h-6" data-id="element-300" /> : <MenuIcon className="w-6 h-6" data-id="element-301" />}
        </button>
      </div>
      {/* Mobile Dropdown Menu */}
      <AnimatePresence data-id="element-302">
        {isOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="lg:hidden border-t-2 border-black bg-white overflow-hidden" data-id="element-303">
            <div className="px-6 py-4 space-y-2" data-id="element-304">
              {navItems.map((item, index) => <motion.button key={item.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.05
          }} onClick={() => handleNavClick(item.id)} className="w-full text-left px-4 py-3 text-sm font-bold uppercase tracking-wider border-2 border-black hover:bg-black hover:text-white transition-all duration-200" data-id="element-305">
                  {item.label}
                </motion.button>)}
              {/* Mobile CTAs */}
              <button onClick={() => handleNavClick('waitlist')} className="w-full px-4 py-3 bg-black text-white font-black text-sm uppercase tracking-wider rounded-full mt-4" data-id="element-306">
                {t('nav.joinWaitlist')}
              </button>
              {/* Mobile Language Toggle */}
              <div className="flex justify-center pt-4 border-t border-gray-300" data-id="element-307">
                <LanguageToggle data-id="element-308" />
              </div>
              {/* Mobile Social Links */}
              <div className="flex justify-center gap-6 pt-4 border-t border-gray-300" data-id="element-309">
                {socialLinks.map(social => <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" className="text-black hover:text-gray-600 transition-all duration-200" aria-label={social.name} data-id="element-310">
                    {social.icon}
                  </a>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}